import { Provider } from 'react-redux';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { BoatInfoForm } from './components/forms/BoatInfoForm';
import { createTheme } from '@mui/material/styles';
import { MainLayout } from './components/layout/MainLayout';
import { BoatInfoProvider } from './contexts/BoatContext';
import { SessionProvider } from './contexts/SessionContext';
import { AuthProvider } from './contexts/AuthContext';
import { Dashboard } from './components/Dashboard';
import { ApiDocumentationPage } from './pages/ApiDocumentationPage';
import { AdminPage } from './pages/AdminPage';
import { LoginForm } from './components/LoginForm';
import { ProtectedRoute } from './components/ProtectedRoute';
import { MadebbApp } from './madebb/MadebbApp';
import { OffshoreApp } from './os/OffshoreApp';
import { store } from './store/store';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
  },
});

// Fallback rendered by React Router when /shop/* is requested but
// RETAIL_WEB_URL is not configured (server-side redirect not active).
// When RETAIL_WEB_URL IS set, the Express 307 fires before React loads
// so this component is never reached in production.
function ShopUnavailable() {
  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h2>Shop coming soon</h2>
      <p>The Made.bb shop is not available in this environment yet.</p>
      <a href="/">← Back to app</a>
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <SessionProvider>
            <BoatInfoProvider>
              <Router>
                <Routes>
                  {/* Made.bb Application Routes */}
                  <Route path="/madebb/*" element={<MadebbApp />} />

                  {/* Offshore Sails Application Routes */}
                  <Route path="/os/*" element={<OffshoreApp />} />

                  {/* Sail UI Routes */}
                  {/* Public login route */}
                  <Route path="/login" element={<LoginForm />} />

                  {/* Protected routes */}
                  <Route path="/" element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Dashboard />
                      </MainLayout>
                    </ProtectedRoute>
                  } />

                  <Route path="/boat-info" element={
                    <ProtectedRoute>
                      <MainLayout>
                        <BoatInfoForm />
                      </MainLayout>
                    </ProtectedRoute>
                  } />

                  {/* API documentation - available to all users */}
                  <Route path="/api-docs" element={
                    <ProtectedRoute>
                      <MainLayout>
                        <ApiDocumentationPage />
                      </MainLayout>
                    </ProtectedRoute>
                  } />

                  {/* Admin-only route for API administration */}
                  <Route path="/admin" element={
                    <ProtectedRoute adminOnly>
                      <MainLayout>
                        <AdminPage />
                      </MainLayout>
                    </ProtectedRoute>
                  } />

                  {/* /shop/* — server redirects to retail-web when RETAIL_WEB_URL is set.
                      This catch route prevents React Router's "No routes matched"
                      blank screen in environments where RETAIL_WEB_URL is not configured. */}
                  <Route path="/shop/*" element={<ShopUnavailable />} />
                </Routes>
              </Router>
            </BoatInfoProvider>
          </SessionProvider>
        </AuthProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;